import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, Printer, Info } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart,
} from "recharts";
import Seo from "@/components/Seo";
import ToolPrimer from "@/components/ToolPrimer";

/* Coefficienti di trasformazione INPS (values apply 2023-2024 official table).
   Ages outside [57,71] are clamped per the original Python. */
const COEFF_TABLE = {
  57: 0.04720, 58: 0.04860, 59: 0.05002, 60: 0.05148, 61: 0.05301,
  62: 0.05462, 63: 0.05632, 64: 0.05813, 65: 0.06005, 66: 0.06210,
  67: 0.06430, 68: 0.06663, 69: 0.06908, 70: 0.07176, 71: 0.07468,
};

const DEFAULTS = {
  workerType: "Dipendente",
  currentAge: 40,
  retirementAge: 67,
  ral: 35000,
  tassoSostituzione: 60,   // % INPS replacement rate
  targetIncomeRatio: 80,   // % of RAL desired at retirement
  inflazione: 2,           // %
  rendimento: 4,           // %
};

function simulate(s) {
  const anniPensione = Math.max(1, s.retirementAge - s.currentAge);
  const mesi = anniPensione * 12;

  const tasso = s.tassoSostituzione / 100;
  const target = s.targetIncomeRatio / 100;
  const infl = s.inflazione / 100;
  const rend = s.rendimento / 100;

  const pensioneAnnua = s.ral * tasso;
  const pensioneMensile = pensioneAnnua / 13;

  const redditoTargetAnnuo = s.ral * target;
  const redditoTargetMensile = redditoTargetAnnuo / 13;

  const gapAnnuo = Math.max(redditoTargetAnnuo - pensioneAnnua, 0);
  const gapMensile = gapAnnuo / 13;

  const etaRif = Math.min(71, Math.max(57, s.retirementAge));
  const coefficiente = COEFF_TABLE[etaRif] || 0.0561;

  const capitaleOggi = gapAnnuo / coefficiente;
  const montanteFuturo = capitaleOggi * Math.pow(1 + infl, anniPensione);

  // TFR only for Dipendente
  let montanteTfr = 0;
  if (s.workerType === "Dipendente") {
    const tfrRate = 0.0691;
    for (let y = 0; y < anniPensione; y++) {
      montanteTfr = (montanteTfr + s.ral * tfrRate) * (1 + rend);
    }
  }

  const montanteTarget = Math.max(montanteFuturo - montanteTfr, 0);
  const rMensile = Math.pow(1 + rend, 1 / 12) - 1;
  let risparmioMensile = 0;
  if (montanteTarget > 0) {
    risparmioMensile =
      rMensile === 0
        ? montanteTarget / mesi
        : (montanteTarget * rMensile) / (Math.pow(1 + rMensile, mesi) - 1);
  }

  // Series
  const capitalSeries = new Array(mesi + 1);
  let capital = 0;
  capitalSeries[0] = { month: 0, year: s.currentAge, capital: 0 };
  for (let m = 1; m <= mesi; m++) {
    capital = capital * (1 + rMensile) + risparmioMensile;
    capitalSeries[m] = {
      month: m,
      year: +(s.currentAge + m / 12).toFixed(2),
      capital,
    };
  }

  return {
    anniPensione, mesi,
    pensioneAnnua, pensioneMensile,
    redditoTargetAnnuo, redditoTargetMensile,
    gapAnnuo, gapMensile,
    coefficiente, etaRif,
    capitaleOggi, montanteFuturo,
    montanteTfr, montanteTarget,
    risparmioMensile,
    capitalSeries,
  };
}

const eur = (n) => "\u20AC" + new Intl.NumberFormat("en-IE", { maximumFractionDigits: 0 }).format(Math.round(n));
const eurCompact = (n) => "\u20AC" + new Intl.NumberFormat("en-IE", { notation: "compact", maximumFractionDigits: 1 }).format(n);

export default function PensionPlanning() {
  const [s, setS] = useState(DEFAULTS);
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const r = useMemo(() => simulate(s), [s]);

  return (
    <main data-testid="pension-tool" className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
      <Seo
        title="Pianificazione previdenziale INPS"
        description="Dal tasso di sostituzione INPS al risparmio mensile per colmare il gap previdenziale. Include TFR per dipendenti e coefficienti di trasformazione."
        path="/tools/pension-planning"
      />
      <Link
        to="/tools"
        data-testid="pen-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-colors mb-10 no-print"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Tutti gli strumenti
      </Link>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-3">
            Strumento finanziario · Pianificazione previdenziale
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.02] tracking-tighter">
            Colmare il
            <br />
            <span className="italic text-primary">gap previdenziale.</span>
          </h1>
        </div>
        <p className="max-w-md text-sm text-foreground/60 leading-relaxed">
          Parte dal <em>tasso di sostituzione</em> del tuo MyINPS, proietta la
          differenza tra la pensione che riceverai e il reddito che vorrai, e
          calcola quanto devi accantonare ogni mese per colmarla.
        </p>
      </header>

      <div className="mb-10 border border-dashed border-border rounded-sm p-5 flex items-start gap-4 bg-secondary/40 no-print" data-testid="pen-hint">
        <Info size={18} strokeWidth={1.5} className="text-primary mt-0.5 flex-shrink-0" />
        <p className="text-sm text-foreground/75 leading-relaxed">
          Prima di iniziare, esegui la <strong>simulazione della pensione</strong> sul tuo{" "}
          <a href="https://serviziweb2.inps.it" target="_blank" rel="noreferrer" className="text-primary underline underline-offset-4">MyINPS</a>.
          Leggi il <em>tasso di sostituzione</em> (tipicamente 55–70% per i lavoratori dipendenti, inferiore per gli autonomi) e inseriscilo qui sotto.
        </p>
      </div>

      <div className="print-only mb-8">
        <div className="text-xs uppercase tracking-widest text-foreground/60">Moneyssance · Report di pianificazione previdenziale</div>
        <div className="text-sm text-foreground/60 mt-1">Generato il {new Date().toLocaleDateString("it-IT")}</div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 xl:gap-10">
        {/* Inputs */}
        <aside className="xl:col-span-4 space-y-5 no-print" data-testid="pen-inputs">
          <Panel title="Profilo">
            <div className="flex gap-2 mb-5">
              {["Dipendente", "Autonomo"].map((w) => (
                <button
                  key={w}
                  onClick={() => set({ workerType: w })}
                  data-testid={`pen-worker-${w.toLowerCase()}`}
                  className={`flex-1 text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 rounded-full border transition-colors ${
                    s.workerType === w
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-foreground/70 hover:border-primary hover:text-primary"
                  }`}
                >{w}</button>
              ))}
            </div>
            <NumField label="Età attuale" value={s.currentAge} min={18} max={80} step={1}
              onChange={(v) => set({ currentAge: v, retirementAge: Math.max(v + 1, s.retirementAge) })} testId="pen-age" />
            <NumField label="Età di pensionamento" value={s.retirementAge} min={s.currentAge + 1} max={80} step={1}
              onChange={(v) => set({ retirementAge: v })} testId="pen-retire-age" />
            <NumField label="RAL — reddito annuo lordo (EUR)" value={s.ral} min={0} max={500000} step={500}
              onChange={(v) => set({ ral: v })} testId="pen-ral" last />
          </Panel>

          <Panel title="Assunzioni previdenziali">
            <SliderField label="Tasso di sostituzione INPS" value={s.tassoSostituzione} min={20} max={90} step={0.5}
              display={`${s.tassoSostituzione.toFixed(1)}%`} onChange={(v) => set({ tassoSostituzione: v })} testId="pen-tasso" />
            <SliderField label="Reddito target in pensione" value={s.targetIncomeRatio} min={30} max={100} step={1}
              display={`${s.targetIncomeRatio}% del reddito attuale`} onChange={(v) => set({ targetIncomeRatio: v })} testId="pen-target" />
            <SliderField label="Inflazione attesa" value={s.inflazione} min={0} max={10} step={0.1}
              display={`${s.inflazione.toFixed(1)}%`} onChange={(v) => set({ inflazione: v })} testId="pen-infl" />
            <SliderField label="Rendimento atteso" value={s.rendimento} min={0} max={12} step={0.1}
              display={`${s.rendimento.toFixed(1)}%`} onChange={(v) => set({ rendimento: v })} testId="pen-rend" last />
          </Panel>

          <div className="border border-border rounded-sm p-5 bg-secondary/30">
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50 mb-2">Coefficiente di trasformazione</div>
            <div className="flex items-baseline justify-between">
              <div className="font-serif text-2xl tabular-nums">{(r.coefficiente * 100).toFixed(3)}%</div>
              <div className="text-[10px] uppercase tracking-widest text-foreground/50">età di riferimento {r.etaRif}</div>
            </div>
            <p className="mt-3 text-[11px] text-foreground/55 leading-relaxed">
              Tabella INPS 2023–24 · usata per convertire il gap annuo in capitale necessario oggi.
            </p>
          </div>
        </aside>

        {/* Outputs */}
        <section className="xl:col-span-8 space-y-6" data-testid="pen-outputs">
          {/* Hero summary */}
          <div className="border border-border rounded-sm p-8 md:p-10 bg-background">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Big label="Risparmio mensile richiesto" value={eur(r.risparmioMensile)} accent testId="pen-monthly-savings" sub={`per ${r.anniPensione} anni`} />
              <Big label="Gap previdenziale annuo" value={eur(r.gapAnnuo)} testId="pen-gap" sub={`${eur(r.gapMensile)} / mese`} />
              <div className="flex flex-col items-start md:items-end justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 md:text-right">
                  {s.workerType} · {r.anniPensione} anni alla pensione
                </div>
                <button
                  onClick={() => window.print()}
                  data-testid="pen-print"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors no-print"
                >
                  <Printer size={14} strokeWidth={1.5} /> Salva PDF
                </button>
              </div>
            </div>
          </div>

          {/* Detailed breakdown table */}
          <div className="border border-border rounded-sm bg-background overflow-hidden" data-testid="pen-table">
            <div className="px-6 md:px-8 py-5 border-b border-border">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/70">Dettaglio previdenziale</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-foreground/50 bg-secondary/40">
                  <th className="text-left px-6 md:px-8 py-3 font-normal">Voce</th>
                  <th className="text-right px-4 py-3 font-normal">Annuo</th>
                  <th className="text-right px-6 md:px-8 py-3 font-normal">Mensile</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Pensione INPS" annual={r.pensioneAnnua} monthly={r.pensioneMensile} />
                <Row label="Reddito desiderato" annual={r.redditoTargetAnnuo} monthly={r.redditoTargetMensile} />
                <Row label="Gap previdenziale" annual={r.gapAnnuo} monthly={r.gapMensile} highlight />
              </tbody>
            </table>
            <div className="border-t border-border grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-6">
              <Cell label="Capitale necessario oggi" value={eur(r.capitaleOggi)} testId="pen-cap-today" />
              <Cell label="Capitale alla pensione (rivalutato)" value={eur(r.montanteFuturo)} testId="pen-cap-future" />
              <Cell label="TFR stimato" value={eur(r.montanteTfr)} testId="pen-tfr" sub={s.workerType === "Autonomo" ? "n.d. (autonomo)" : `${(0.0691 * 100).toFixed(2)}% RAL/anno`} />
            </div>
            <div className="border-t border-border px-6 md:px-8 py-5 flex items-baseline justify-between bg-primary/5">
              <div className="text-xs uppercase tracking-[0.25em] text-primary">Obiettivo previdenziale netto</div>
              <div className="font-serif text-3xl md:text-4xl text-primary tabular-nums" data-testid="pen-target-capital">{eur(r.montanteTarget)}</div>
            </div>
          </div>

          {/* Chart */}
          <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="pen-chart">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">Evoluzione del capitale previdenziale</div>
              <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-foreground/60">
                <span className="inline-flex items-center gap-2"><span className="w-4 h-0.5 bg-primary" /> Accumulo</span>
                <span className="inline-flex items-center gap-2"><span className="w-4 h-0.5 border-t-2 border-dashed border-destructive" /> Obiettivo</span>
              </div>
            </div>
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={r.capitalSeries.filter((_, i) => i % 3 === 0 || i === r.capitalSeries.length - 1)} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="capG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                  <XAxis
                    dataKey="year"
                    type="number"
                    domain={[s.currentAge, s.retirementAge]}
                    tickFormatter={(v) => `${Math.round(v)}`}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    label={{ value: "Età", position: "insideBottom", offset: -4, fill: "hsl(var(--muted-foreground))", fontSize: 10, style: { textTransform: "uppercase", letterSpacing: "0.15em" } }}
                  />
                  <YAxis tickFormatter={eurCompact} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} width={65} />
                  <Tooltip content={<CapTip currentAge={s.currentAge} />} />
                  <Area type="monotone" dataKey="capital" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#capG)" isAnimationActive={false} />
                  <ReferenceLine
                    y={r.montanteTarget}
                    stroke="hsl(var(--destructive))"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    label={{ value: `Obiettivo ${eurCompact(r.montanteTarget)}`, fill: "hsl(var(--destructive))", fontSize: 11, position: "insideTopRight" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <blockquote className="mt-6 max-w-3xl mx-auto text-center font-serif text-xl md:text-2xl italic text-foreground/75 leading-snug">
            "Il tempo è il vero alleato: piccoli passi mensili, ripetuti, creano meraviglie."
          </blockquote>
        </section>
      </div>

      <ToolPrimer
        eyebrow="Come funziona lo strumento"
        title="Colmare il gap, un mese alla volta."
        sections={[
          {
            heading: "Cosa fa questo strumento",
            body: "Parte da un dato reale — il tasso di sostituzione che INPS ti mostra su MyINPS — e proietta la differenza tra la pensione che riceverai e il reddito che vorresti mantenere in età di pensionamento. Da questa differenza ricava il capitale integrativo necessario e il risparmio mensile che serve accantonare, oggi, per costruirlo entro la data del ritiro. È lo scheletro di un piano previdenziale onesto: nessuna promessa, solo aritmetica.",
          },
          {
            heading: "Le ipotesi che contano",
            body: "Il tasso di sostituzione dipende dagli anni di contribuzione, dalla retribuzione media e dalla categoria — tipicamente 55–70% per i lavoratori dipendenti, inferiore per gli autonomi. Il reddito target in pensione è espresso come percentuale del reddito attuale: la letteratura suggerisce 70–80% per mantenere lo stesso tenore di vita, dato che alcune spese (mutuo, figli, contributi) tendono a esaurirsi. Il rendimento atteso è al netto dell'inflazione — un reale, non un nominale — perché ciò che conta al ritiro è il potere d'acquisto.",
          },
          {
            heading: "Come leggere l'output",
            body: "Il risparmio mensile richiesto è la cifra da destinare, ogni mese, a un piano previdenziale integrativo (fondo pensione, PIP, piano di accumulo dedicato) per colmare il gap. Il grafico mostra come il capitale cresce fino alla data di pensionamento e la linea tratteggiata segna il montante obiettivo: se la curva la incontra prima del ritiro, sei in anticipo; se la sfiora appena, il piano è calibrato al minimo indispensabile.",
          },
        ]}
        caveat="Proiezione indicativa, non consulenza. Le regole INPS cambiano nel tempo, i rendimenti reali non sono garantiti e il tasso di sostituzione stimato oggi può differire da quello effettivo al momento del pensionamento. Verifica sempre la simulazione ufficiale su MyINPS prima di prendere decisioni definitive."
      />
    </main>
  );
}

/* ---------------- Small UI helpers ---------------- */
function Panel({ title, children }) {
  return (
    <div className="border border-border rounded-sm p-5 bg-background">
      <div className="text-xs uppercase tracking-[0.25em] text-foreground/70 mb-4">{title}</div>
      {children}
    </div>
  );
}
function NumField({ label, value, min, max, step, onChange, testId, last }) {
  const [raw, setRaw] = useState(String(value));
  useEffect(() => { setRaw(String(value)); }, [value]);
  const commit = () => {
    if (raw === "" || raw === "-") { setRaw(String(value)); return; }
    const n = Number(raw);
    if (!Number.isFinite(n)) { setRaw(String(value)); return; }
    const clamped = Math.max(min, Math.min(max, n));
    setRaw(String(clamped));
    if (clamped !== value) onChange(clamped);
  };
  return (
    <div className={last ? "" : "mb-5"}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-foreground/70 mb-2">{label}</div>
      <input type="number" value={raw} min={min} max={max} step={step}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={testId}
        className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none py-1.5 text-base font-serif tabular-nums" />
    </div>
  );
}
function SliderField({ label, value, min, max, step, display, onChange, testId, last }) {
  return (
    <div className={last ? "" : "mb-5"}>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[11px] uppercase tracking-[0.18em] text-foreground/70">{label}</label>
        <span className="text-sm tabular-nums font-serif">{display}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testId} />
    </div>
  );
}
function Big({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">{label}</div>
      <div className={`font-serif text-4xl md:text-5xl mt-3 tracking-tighter ${accent ? "text-primary" : ""}`}>{value}</div>
      {sub && <div className="mt-2 text-xs text-foreground/50">{sub}</div>}
    </div>
  );
}
function Row({ label, annual, monthly, highlight }) {
  return (
    <tr className={`border-t border-border/60 ${highlight ? "bg-accent/5" : ""}`}>
      <td className={`px-6 md:px-8 py-4 font-serif ${highlight ? "text-accent font-medium" : ""}`}>{label}</td>
      <td className="text-right px-4 py-4 tabular-nums">{eur(annual)}</td>
      <td className="text-right px-6 md:px-8 py-4 tabular-nums">{eur(monthly)}</td>
    </tr>
  );
}
function Cell({ label, value, sub, testId }) {
  return (
    <div className="px-6 md:px-8 py-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50">{label}</div>
      <div className="font-serif text-xl md:text-2xl mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-foreground/45 mt-1">{sub}</div>}
    </div>
  );
}
function CapTip({ active, payload, currentAge }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">
        Età {d.year.toFixed(1)} · mese {d.month}
      </div>
      <div className="font-serif text-lg text-primary">{eur(d.capital)}</div>
    </div>
  );
}
